import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Rating
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EuroIcon from '@mui/icons-material/Euro';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../../contexts/AuthContext';
import { ROLES } from '../../utils/roles';
import { formatDate } from '../../utils/dateUtils';
import { authService } from '../../services/authService';

function EntrepriseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [entreprise, setEntreprise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offres, setOffres] = useState([]);
  const [candidaturesCount, setCandidaturesCount] = useState({});
  const [totalCandidatures, setTotalCandidatures] = useState(0);
  const [moyenneEvaluation, setMoyenneEvaluation] = useState(0);
  const [nombreEvaluations, setNombreEvaluations] = useState(0);
  const [userRating, setUserRating] = useState(null);
  const { currentUser, userRole } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupération des détails de l'entreprise
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}`, {
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error("Cette entreprise n'existe pas");
        }

        const entrepriseData = await response.json();
        setEntreprise(entrepriseData);

        // Récupération des offres de l'entreprise
        const offresResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}/offres`, {
          headers: {
            'Accept': 'application/json'
          }
        });

        if (offresResponse.ok) {
          const offresData = await offresResponse.json();
          const offresArray = Array.isArray(offresData) ? offresData : 
                            offresData.offres ? offresData.offres :
                            offresData.data ? offresData.data : [];
          
          setOffres(offresArray);
          
          // Récupérer les statistiques pour chaque offre
          const statsPromises = offresArray.map(offre => 
            fetch(`${process.env.REACT_APP_API_BASE_URL}/offres/${offre.id_offres}/stats`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            }).then(res => res.json())
            .catch(() => ({ nombreCandidatures: 0 }))
          );

          const statsResults = await Promise.all(statsPromises);
          const candidaturesData = {};
          let total = 0;
          
          statsResults.forEach((stat, index) => {
            const nombreCandidatures = stat.nombreCandidatures || 0;
            candidaturesData[offresArray[index].id_offres] = nombreCandidatures;
            total += nombreCandidatures;
          });
          
          setCandidaturesCount(candidaturesData);
          setTotalCandidatures(total);
        } else {
          setOffres([]);
        }

        // Récupération de la moyenne des évaluations (accessible à tous)
        const moyenneResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}/evaluations/moyenne`
        );

        if (moyenneResponse.ok) {
          const moyenneData = await moyenneResponse.json();
          setMoyenneEvaluation(moyenneData.moyenne);
        }

        // Si l'utilisateur est admin ou pilote, récupérer son évaluation
        if (userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) {
          const endpoint = userRole === ROLES.ADMIN 
            ? '/admin/entreprises/evaluations/me' 
            : '/pilote/entreprises/evaluations/me';
          
          const evaluationsResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
            {
              headers: {
                'Authorization': `Bearer ${authService.getToken()}`
              }
            }
          );

          if (evaluationsResponse.ok) {
            const data = await evaluationsResponse.json();
            const userEval = data.evaluations.find(e => 
              e.entreprise.id === parseInt(id)
            );
            if (userEval) {
              setUserRating({ id: userEval.id, note: userEval.note });
            }
          }

          // Récupérer le nombre total d'évaluations séparément
          const totalEvaluationsResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}/evaluations/count`
          );

          if (totalEvaluationsResponse.ok) {
            const countData = await totalEvaluationsResponse.json();
            setNombreEvaluations(countData.count);
          }
        }

      } catch (err) {
        console.error("Erreur lors du chargement:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentUser, userRole]);

  const handleRating = async (newValue) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}/evaluer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({ note: newValue })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de l\'évaluation');
      }

      const data = await response.json();
      setUserRating({ id: data.evaluation.id, note: newValue });

      // Mettre à jour la moyenne
      const moyenneResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/entreprises/${id}/evaluations/moyenne`
      );
      
      if (moyenneResponse.ok) {
        const moyenneData = await moyenneResponse.json();
        setMoyenneEvaluation(moyenneData.moyenne);
      }

    } catch (error) {
      console.error('Erreur lors de l\'évaluation:', error);
    }
  };

  // Déterminer le chemin de retour avec la page
  const getRetourInfo = () => {
    const previousPath = location.state?.from || '/entreprises';
    const page = location.state?.page || 1;
    
    return {
      text: 'Retour aux entreprises',
      path: `/entreprises?page=${page}`
    };
  };

  const { text: retourText, path: retourPath } = getRetourInfo();

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress color="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(retourPath)}
            color="primary"
          >
            {retourText}
          </Button>
        </Box>
      </Container>
    );
  }

  if (!entreprise) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(retourPath)}
        color="primary"
      >
        {retourText}
      </Button>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px'
            }}
          >
            {entreprise.logo ? (
              <img
                src={entreprise.logo}
                alt={entreprise.nom}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : (
              <Typography variant="body1" color="text.secondary">
                Aucun logo disponible
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Typography variant="h3" component="h1" gutterBottom>
            {entreprise.nom}
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" paragraph>
              {entreprise.description}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography component="span">
                Note moyenne : {moyenneEvaluation.toFixed(1)} / 5
                {nombreEvaluations > 0 && ` (${nombreEvaluations} évaluation${nombreEvaluations !== 1 ? 's' : ''})`}
              </Typography>
              {(userRole === ROLES.ADMIN || userRole === ROLES.PILOTE) && (
                <Rating
                  value={userRating?.note || 0}
                  onChange={(event, newValue) => handleRating(newValue)}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ color: 'primary.main' }} />
              <Typography>
                <strong>Secteur:</strong> {entreprise.secteur}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ color: 'primary.main' }} />
              <Typography>
                <strong>Localisation:</strong> {entreprise.localisation}
              </Typography>
            </Box>

            {entreprise.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: 'primary.main' }} />
                <Typography>
                  <strong>Email:</strong>{' '}
                  <a 
                    href={`mailto:${entreprise.email}`}
                    sx={{ color: 'primary.main' }}
                  >
                    {entreprise.email}
                  </a>
                </Typography>
              </Box>
            )}

            {entreprise.telephone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ color: 'primary.main' }} />
                <Typography>
                  <strong>Téléphone:</strong>{' '}
                  <a 
                    href={`tel:${entreprise.telephone}`}
                    sx={{ color: 'primary.main' }}
                  >
                    {entreprise.telephone}
                  </a>
                </Typography>
              </Box>
            )}

            {entreprise.siteWeb && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon sx={{ color: 'primary.main' }} />
                <Typography>
                  <strong>Site Web:</strong>{' '}
                  <a 
                    href={entreprise.siteWeb}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main' }}
                  >
                    {entreprise.siteWeb}
                  </a>
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: 'primary.main' }} />
              <Typography>
                <strong>Total des candidatures:</strong> {totalCandidatures}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Typography variant="h4" sx={{ mt: 6, mb: 3 }}>
        Offres de stage disponibles
      </Typography>
      
      {offres.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          Aucune offre de stage disponible actuellement.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {offres.map((offre) => (
            <Grid item xs={12} md={6} key={offre.id_offres}>
              <Card 
                sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    cursor: 'pointer'
                  }
                }}
                onClick={() => navigate(`/offres/${offre.id_offres}`, { 
                  state: { 
                    from: `/entreprises/${id}`,
                    page: location.state?.page || 1,
                    entrepriseId: id
                  } 
                })}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {offre.titre}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {offre.description}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {offre.technologies.map((tech, index) => (
                      <Chip 
                        key={tech.id_techno}
                        label={tech.nom_techno}
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTimeIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="body2">
                        {offre.duree}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EuroIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="body2">
                        {offre.remuneration}€
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="body2">
                        Début : {formatDate(offre.dateDebut)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="body2">
                        {candidaturesCount[offre.id_offres] || 0} candidature{(candidaturesCount[offre.id_offres] || 0) !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default EntrepriseDetail; 